/**
 * Learner image with expand-to-fullscreen control.
 * Desktop: expand icon on hover. Touch devices: tap image toggles icon visibility.
 * Fullscreen: pinch-to-zoom, pan when zoomed, and layout updates on orientation change.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, Maximize2, X } from 'lucide-react';
import { useHistoryBackClose } from '../../hooks/useHistoryBackClose';
import { useOrientation, useTouchFriendly } from '../shared/MobileResponsive';

/** Show spinner only if the image is still loading after this delay. */
const SLOW_LOAD_SPINNER_MS = 300;
/** Transient decode/network failures often succeed on a quick retry (modal open race). */
const IMAGE_LOAD_MAX_ATTEMPTS = 3;

export interface LearnerExpandableImageProps {
  src: string;
  alt: string;
  className?: string;
  /** When true, clicking the image opens fullscreen (not just the expand button). */
  openOnImageClick?: boolean;
  /** When true, wrapper fills parent width/height (e.g. fixed-aspect cover slot). */
  fillContainer?: boolean;
  /** Smaller expand control for thumbnails (vocab, word help, Leitner). */
  compact?: boolean;
  onLoad?: React.ReactEventHandler<HTMLImageElement>;
  onError?: React.ReactEventHandler<HTMLImageElement>;
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const TAP_MOVE_THRESHOLD = 10;
const DOUBLE_TAP_MS = 500;
const ZOOM_TOGGLE_COOLDOWN_MS = 400;

function touchDistance(t1: Touch, t2: Touch): number {
  return Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
}

interface FullscreenImageOverlayProps {
  src: string;
  alt: string;
  onClose: () => void;
}

const FullscreenImageOverlay: React.FC<FullscreenImageOverlayProps> = ({ src, alt, onClose }) => {
  useHistoryBackClose(true, onClose, 'learner-expandable-image');
  const { orientation } = useOrientation();
  const gestureAreaRef = useRef<HTMLDivElement>(null);
  const [viewport, setViewport] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
    top: 0,
    left: 0
  }));
  const [scale, setScale] = useState(MIN_ZOOM);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const scaleRef = useRef(MIN_ZOOM);
  const translateRef = useRef({ x: 0, y: 0 });
  const gestureRef = useRef<{
    mode: 'none' | 'pinch' | 'pan' | 'tap';
    startDistance: number;
    startScale: number;
    startTranslate: { x: number; y: number };
    lastPan: { x: number; y: number };
    moved: boolean;
  }>({
    mode: 'none',
    startDistance: 0,
    startScale: MIN_ZOOM,
    startTranslate: { x: 0, y: 0 },
    lastPan: { x: 0, y: 0 },
    moved: false
  });
  const lastTapRef = useRef(0);
  const lastZoomToggleAtRef = useRef(0);
  const mousePanActiveRef = useRef(false);
  const [isMouseDragging, setIsMouseDragging] = useState(false);

  const applyTransform = useCallback((nextScale: number, nextTranslate: { x: number; y: number }) => {
    scaleRef.current = nextScale;
    translateRef.current = nextTranslate;
    setScale(nextScale);
    setTranslate(nextTranslate);
  }, []);

  const updateViewport = useCallback(() => {
    const vv = window.visualViewport;
    setViewport({
      width: vv?.width ?? window.innerWidth,
      height: vv?.height ?? window.innerHeight,
      top: vv?.offsetTop ?? 0,
      left: vv?.offsetLeft ?? 0
    });
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    updateViewport();
    window.visualViewport?.addEventListener('resize', updateViewport);
    window.visualViewport?.addEventListener('scroll', updateViewport);
    window.addEventListener('resize', updateViewport);
    window.addEventListener('orientationchange', updateViewport);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
      window.visualViewport?.removeEventListener('resize', updateViewport);
      window.visualViewport?.removeEventListener('scroll', updateViewport);
      window.removeEventListener('resize', updateViewport);
      window.removeEventListener('orientationchange', updateViewport);
    };
  }, [onClose, updateViewport]);

  const toggleZoom = useCallback(() => {
    const now = Date.now();
    if (now - lastZoomToggleAtRef.current < ZOOM_TOGGLE_COOLDOWN_MS) return;
    lastZoomToggleAtRef.current = now;
    if (scaleRef.current > MIN_ZOOM) {
      applyTransform(MIN_ZOOM, { x: 0, y: 0 });
    } else {
      applyTransform(2, { x: 0, y: 0 });
    }
    lastTapRef.current = 0;
  }, [applyTransform]);

  useEffect(() => {
    applyTransform(MIN_ZOOM, { x: 0, y: 0 });
    updateViewport();
  }, [orientation, applyTransform, updateViewport]);

  useEffect(() => {
    const el = gestureAreaRef.current;
    if (!el) return;

    const clampScale = (value: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));

    const onTouchStart = (e: TouchEvent) => {
      e.stopPropagation();
      if (e.touches.length === 2) {
        const t0 = e.touches[0];
        const t1 = e.touches[1];
        if (!t0 || !t1) return;
        gestureRef.current = {
          mode: 'pinch',
          startDistance: touchDistance(t0, t1),
          startScale: scaleRef.current,
          startTranslate: { ...translateRef.current },
          lastPan: { x: 0, y: 0 },
          moved: false
        };
        return;
      }
      if (e.touches.length === 1) {
        const touch = e.touches[0];
        if (!touch) return;
        gestureRef.current = {
          mode: 'tap',
          startDistance: 0,
          startScale: scaleRef.current,
          startTranslate: { ...translateRef.current },
          lastPan: { x: touch.clientX, y: touch.clientY },
          moved: false
        };
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      e.stopPropagation();
      const gesture = gestureRef.current;
      if (gesture.mode === 'pinch' && e.touches.length === 2) {
        e.preventDefault();
        const t0 = e.touches[0];
        const t1 = e.touches[1];
        if (!t0 || !t1) return;
        const distance = touchDistance(t0, t1);
        if (gesture.startDistance > 0) {
          const nextScale = clampScale(gesture.startScale * (distance / gesture.startDistance));
          if (nextScale <= MIN_ZOOM) {
            applyTransform(MIN_ZOOM, { x: 0, y: 0 });
          } else {
            applyTransform(nextScale, translateRef.current);
          }
        }
        return;
      }
      if (gesture.mode === 'tap' && e.touches.length === 1 && scaleRef.current > MIN_ZOOM) {
        const touch = e.touches[0];
        if (!touch) return;
        const dx = touch.clientX - gesture.lastPan.x;
        const dy = touch.clientY - gesture.lastPan.y;
        if (Math.hypot(dx, dy) > TAP_MOVE_THRESHOLD) {
          gesture.mode = 'pan';
          gesture.moved = true;
          gesture.lastPan = { x: touch.clientX, y: touch.clientY };
        }
      }
      if (gesture.mode === 'pan' && e.touches.length === 1) {
        e.preventDefault();
        const touch = e.touches[0];
        if (!touch) return;
        const dx = touch.clientX - gesture.lastPan.x;
        const dy = touch.clientY - gesture.lastPan.y;
        gesture.lastPan = { x: touch.clientX, y: touch.clientY };
        applyTransform(scaleRef.current, {
          x: translateRef.current.x + dx,
          y: translateRef.current.y + dy
        });
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      e.stopPropagation();
      const gesture = gestureRef.current;

      if (gesture.mode === 'pinch') {
        if (e.touches.length === 0) {
          gestureRef.current.mode = 'none';
          if (scaleRef.current <= MIN_ZOOM) {
            applyTransform(MIN_ZOOM, { x: 0, y: 0 });
          }
        }
        return;
      }

      if (gesture.mode === 'pan') {
        if (e.touches.length === 0) {
          gestureRef.current.mode = 'none';
        }
        return;
      }

      if (gesture.mode === 'tap' && e.changedTouches.length === 1) {
        const now = Date.now();
        if (now - lastTapRef.current < DOUBLE_TAP_MS) {
          e.preventDefault();
          toggleZoom();
        } else {
          lastTapRef.current = now;
        }
        gestureRef.current.mode = 'none';
      }
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!mousePanActiveRef.current) return;
      e.preventDefault();
      const dx = e.clientX - gestureRef.current.lastPan.x;
      const dy = e.clientY - gestureRef.current.lastPan.y;
      gestureRef.current.lastPan = { x: e.clientX, y: e.clientY };
      applyTransform(scaleRef.current, {
        x: translateRef.current.x + dx,
        y: translateRef.current.y + dy
      });
    };

    const onMouseUp = () => {
      if (!mousePanActiveRef.current) return;
      mousePanActiveRef.current = false;
      setIsMouseDragging(false);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0 || scaleRef.current <= MIN_ZOOM) return;
      e.preventDefault();
      e.stopPropagation();
      mousePanActiveRef.current = true;
      setIsMouseDragging(true);
      gestureRef.current.lastPan = { x: e.clientX, y: e.clientY };
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    };

    el.addEventListener('mousedown', onMouseDown);
    el.addEventListener('touchstart', onTouchStart, { passive: false });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: false });
    el.addEventListener('touchcancel', onTouchEnd, { passive: false });
    return () => {
      el.removeEventListener('mousedown', onMouseDown);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      mousePanActiveRef.current = false;
    };
  }, [applyTransform, toggleZoom]);

  return (
    <div
      data-learner-fullscreen-image
      className="fixed z-[200] flex items-center justify-center bg-black/90 p-4 overscroll-none"
      role="dialog"
      aria-modal="true"
      aria-label="Full screen image"
      style={{
        top: viewport.top,
        left: viewport.left,
        width: viewport.width,
        height: viewport.height,
        touchAction: 'none'
      }}
      onClick={onClose}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-4 right-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25"
        aria-label="Close full screen"
      >
        <X className="h-6 w-6" />
      </button>
      <div
        ref={gestureAreaRef}
        className={`flex h-full w-full items-center justify-center overflow-hidden ${
          scale > MIN_ZOOM ? (isMouseDragging ? 'cursor-grabbing' : 'cursor-grab') : ''
        }`}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          toggleZoom();
        }}
        style={{ touchAction: 'none' }}
      >
        <img
          src={src}
          alt={alt}
          draggable={false}
          className="max-h-full max-w-full select-none object-contain"
          style={{
            transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
            transformOrigin: 'center center',
            touchAction: 'none'
          }}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    </div>
  );
};

export const LearnerExpandableImage: React.FC<LearnerExpandableImageProps> = ({
  src,
  alt,
  className = 'max-w-full h-auto rounded-lg shadow-sm',
  openOnImageClick = false,
  fillContainer = false,
  compact = false,
  onLoad,
  onError
}) => {
  const { isTouchDevice } = useTouchFriendly();
  const imgRef = useRef<HTMLImageElement>(null);
  const loadAttemptRef = useRef(0);
  const retryTimerRef = useRef<number | null>(null);
  const [showExpandIcon, setShowExpandIcon] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  /** Tracks which `src` has finished loading so a src change does not flash the old image. */
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const [showSlowLoader, setShowSlowLoader] = useState(false);
  /** Cache-busting suffix applied only during automatic retries. */
  const [retryToken, setRetryToken] = useState(0);
  const isLoaded = loadedSrc === src;
  const displaySrc =
    retryToken > 0
      ? `${src}${src.includes('?') ? '&' : '?'}_mediaRetry=${retryToken}`
      : src;

  useEffect(() => {
    loadAttemptRef.current = 0;
    setRetryToken(0);
    setShowSlowLoader(false);
    const timer = window.setTimeout(() => setShowSlowLoader(true), SLOW_LOAD_SPINNER_MS);
    const img = imgRef.current;
    // Cached images may finish before onLoad is attached.
    if (img?.complete && img.naturalWidth > 0) {
      setLoadedSrc(src);
      window.clearTimeout(timer);
    }
    return () => {
      window.clearTimeout(timer);
      if (retryTimerRef.current !== null) {
        window.clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, [src]);

  const handleLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      loadAttemptRef.current = 0;
      setLoadedSrc(src);
      setShowSlowLoader(false);
      onLoad?.(e);
    },
    [onLoad, src]
  );

  const handleError = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      if (loadAttemptRef.current < IMAGE_LOAD_MAX_ATTEMPTS - 1) {
        loadAttemptRef.current += 1;
        const attempt = loadAttemptRef.current;
        setShowSlowLoader(true);
        if (retryTimerRef.current !== null) {
          window.clearTimeout(retryTimerRef.current);
        }
        retryTimerRef.current = window.setTimeout(() => {
          setRetryToken(attempt);
        }, 120 * attempt);
        return;
      }
      setLoadedSrc(src);
      setShowSlowLoader(false);
      onError?.(e);
    },
    [onError, src]
  );

  const openFullscreen = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsFullscreen(true);
  }, []);

  const closeFullscreen = useCallback(() => {
    setIsFullscreen(false);
  }, []);

  const expandIconClass = isTouchDevice
    ? showExpandIcon
      ? 'opacity-100'
      : 'opacity-0 pointer-events-none'
    : showExpandIcon
      ? 'opacity-100'
      : 'opacity-0 group-hover:opacity-100';

  return (
    <>
      <div
        className={`group relative max-w-full ${
          fillContainer ? 'block h-full w-full' : 'inline-block'
        }${!isLoaded ? (fillContainer ? '' : ' min-h-16 min-w-16') : ''}`}
        onMouseEnter={!isTouchDevice ? () => setShowExpandIcon(true) : undefined}
        onMouseLeave={!isTouchDevice ? () => setShowExpandIcon(false) : undefined}
      >
        {!isLoaded && showSlowLoader && (
          <div
            className={`pointer-events-none absolute inset-0 z-[1] flex items-center justify-center ${
              fillContainer ? '' : 'min-h-16 min-w-16'
            }`}
            aria-hidden
          >
            <Loader2 className="h-4 w-4 animate-spin text-rose-500 dark:text-rose-400" />
          </div>
        )}
        <img
          ref={imgRef}
          src={displaySrc}
          alt={alt}
          className={`${className}${!isLoaded ? ' opacity-0' : ''}`}
          onLoad={handleLoad}
          onError={handleError}
          onClick={() => {
            if (openOnImageClick) {
              setIsFullscreen(true);
              return;
            }
            if (isTouchDevice) setShowExpandIcon((v) => !v);
          }}
          role={isTouchDevice || openOnImageClick ? 'button' : undefined}
          tabIndex={isTouchDevice || openOnImageClick ? 0 : undefined}
          onKeyDown={
            isTouchDevice || openOnImageClick
              ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (openOnImageClick) setIsFullscreen(true);
                    else setShowExpandIcon((v) => !v);
                  }
                }
              : undefined
          }
        />
        {isLoaded && (
          <button
            type="button"
            onClick={openFullscreen}
            className={`absolute flex items-center justify-center rounded-lg bg-black/55 text-white shadow-md transition-opacity duration-200 hover:bg-black/75 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-400 ${
              compact ? 'bottom-0.5 right-0.5 h-6 w-6' : 'bottom-2 right-2 h-8 w-8'
            } ${expandIconClass}`}
            aria-label="View full screen"
            title="Full screen"
          >
            <Maximize2 className={compact ? 'h-3 w-3' : 'h-4 w-4'} aria-hidden />
          </button>
        )}
      </div>

      {isFullscreen &&
        typeof document !== 'undefined' &&
        createPortal(
          <FullscreenImageOverlay src={src} alt={alt} onClose={closeFullscreen} />,
          document.body
        )}
    </>
  );
};
